import { DateRange } from 'react-day-picker';
import { useCallback, useState, useEffect, useMemo } from 'react';
import {
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subMonths,
  min,
  subDays,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  parse,
  isSameDay
} from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, InfoIcon } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useSearchParams } from 'react-router-dom';
import { useMerchantTimezone } from '@/hooks/use-merchant-timezone';
import { DateTime } from 'luxon';

interface Preset {
  label: string;
  getValue: () => DateRange;
}

const presets: Preset[] = [
  {
    label: 'Today',
    getValue: () => {
      const today = new Date();
      return { from: startOfDay(today), to: endOfDay(today) };
    }
  },
  {
    label: 'Yesterday',
    getValue: () => {
      const yesterday = subDays(new Date(), 1);
      return { from: startOfDay(yesterday), to: endOfDay(yesterday) };
    }
  },
  {
    label: 'Last 7 days',
    getValue: () => {
      const today = new Date();
      return { from: subDays(today, 7), to: today };
    }
  },
  {
    label: 'Last 30 days',
    getValue: () => {
      const today = new Date();
      return { from: subDays(today, 30), to: today };
    }
  },
  {
    label: 'Last 90 days',
    getValue: () => {
      const today = new Date();
      return { from: subDays(today, 90), to: today };
    }
  },
  {
    label: 'Last month',
    getValue: () => {
      const lastMonth = subMonths(new Date(), 1);
      return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
    }
  },
  {
    label: 'Week to date',
    getValue: () => {
      const today = new Date();
      return { from: startOfWeek(today), to: min([endOfWeek(today), today]) };
    }
  },
  {
    label: 'Month to date',
    getValue: () => {
      const today = new Date();
      return { from: startOfMonth(today), to: min([endOfMonth(today), today]) };
    }
  },
  {
    label: 'Quarter to date',
    getValue: () => {
      const today = new Date();
      return { from: startOfQuarter(today), to: min([endOfQuarter(today), today]) };
    }
  },
  {
    label: 'Year to date',
    getValue: () => {
      const today = new Date();
      return { from: startOfYear(today), to: min([endOfYear(today), today]) };
    }
  }
];

interface DatePickerProps {
  value?: DateRange;
  onChange?: (value: DateRange | undefined) => void;
  defaultValue?: DateRange;
  className?: React.HTMLAttributes<HTMLDivElement>['className'];
}

function getMatchingPresetLabel(range: DateRange): string | undefined {
  if (!range.from || !range.to) return 'Last 7 days';

  for (const preset of presets) {
    const presetRange = preset.getValue();
    const isFromSame = presetRange.from && isSameDay(presetRange.from, range.from);
    const isToSame = presetRange.to && isSameDay(presetRange.to, range.to);
    if (isFromSame && isToSame) {
      return preset.label;
    }
  }
  return undefined;
}

export const DatePicker = ({ className, value, onChange, defaultValue }: DatePickerProps) => {
  const { timezone, loading: timezoneLoading } = useMerchantTimezone();
  const defaultDateRange = defaultValue ??
    presets.find((preset) => preset.label === 'Last 7 days')?.getValue() ?? {
      from: new Date(),
      to: new Date()
    };
  const disabledDays = { after: new Date() };

  const [internalDate, setInternalDate] = useState<DateRange | undefined>(defaultDateRange);
  const [tempDate, setTempDate] = useState<DateRange | undefined>(value ?? internalDate);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPresetLabel, setSelectedPresetLabel] = useState<string | undefined>('Last 7 days');

  const date = value ?? internalDate;

  // Format timezone for display
  const formattedTimezone = useMemo(() => {
    if (timezoneLoading) return 'Loading...';

    try {
      // Get the current offset for this timezone
      const zone = DateTime.local().setZone(timezone);
      return zone.offsetNameShort;
    } catch (e) {
      console.error('Error formatting timezone:', e);
      return timezone;
    }
  }, [timezone, timezoneLoading]);

  useEffect(() => {
    if (date?.from && date.to) {
      const label = getMatchingPresetLabel({ from: date.from, to: date.to });
      setSelectedPresetLabel(label);
    }
  }, [date]);

  const handleSelect = (newDate: DateRange | undefined) => {
    if (newDate?.to) {
      setTempDate({
        from: newDate.from,
        to: newDate.to
      });
    } else {
      setTempDate(newDate);
    }
  };

  const handleApply = () => {
    if (onChange) {
      onChange(tempDate);
    } else {
      setInternalDate(tempDate);
    }
    setIsOpen(false);
  };

  return (
    <div className={cn('grid gap-2', className)}>
      <Popover
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
          if (open) {
            setTempDate(date);
          }
        }}
      >
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant="outline"
            className={cn(
              'flex items-center justify-center gap-2',
              !date && 'text-muted-foreground'
            )}
          >
            <CalendarIcon className="h-4 w-4" />
            <span className="hidden sm:inline">
              {date?.from ? (
                date.to ? (
                  <>
                    {selectedPresetLabel ??
                      `${format(date.from, 'LLL dd, y')} - ${format(date.to, 'LLL dd, y')}`}
                  </>
                ) : (
                  format(date.from, 'LLL dd, y')
                )
              ) : (
                <span>Pick a date</span>
              )}
            </span>
            <div className="hidden sm:flex items-center gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-muted-foreground">{formattedTimezone}</span>
                      <InfoIcon className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>All data is displayed in your merchant timezone ({timezone}).</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2 mr-6 rounded-lg" align="start" sideOffset={4}>
          <div className="flex">
            <div className="w-[141px] border-r mr-6">
              <div className="p-2 space-y-0.5">
                {presets.map((preset) => (
                  <Button
                    key={preset.label}
                    variant={selectedPresetLabel === preset.label ? 'secondary' : 'ghost'}
                    className={cn(
                      'w-full justify-start font-normal text-sm h-8',
                      selectedPresetLabel === preset.label && 'bg-accent hover:bg-accent/80',
                      selectedPresetLabel !== preset.label && 'hover:bg-accent/50'
                    )}
                    onClick={() => {
                      const presetValue = preset.getValue();
                      setInternalDate(presetValue);
                      setTempDate(presetValue);
                      setSelectedPresetLabel(preset.label);
                    }}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
            </div>
            <div className="flex flex-col">
              <Calendar
                autoFocus
                mode="range"
                defaultMonth={tempDate?.from}
                selected={tempDate}
                onSelect={handleSelect}
                numberOfMonths={2}
                disabled={disabledDays}
                showOutsideDays={false}
              />
              <div className="flex justify-end gap-2 mt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setIsOpen(false);
                    setTempDate(date);
                  }}
                >
                  Cancel
                </Button>
                <Button size="sm" onClick={handleApply}>
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export function ControlledDatePicker({
  defaultPreviousDays = 7
}: {
  defaultPreviousDays?: number;
}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const fromParam = searchParams.get('from');
  const toParam = searchParams.get('to');

  const dateRange =
    fromParam && toParam
      ? {
          provided: true,
          from: parse(fromParam, 'yyyy-MM-dd', new Date()),
          to: parse(toParam, 'yyyy-MM-dd', new Date())
        }
      : {
          provided: false,
          from: startOfDay(subDays(new Date(), defaultPreviousDays)),
          to: endOfDay(new Date())
        };

  const { from: fromDate, to: toDate, provided } = dateRange;
  useEffect(() => {
    if (!provided) {
      setSearchParams({
        from: format(fromDate, 'yyyy-MM-dd'),
        to: format(toDate, 'yyyy-MM-dd')
      });
    }
  }, [fromDate, toDate, provided, setSearchParams]);

  const handleDateChange = useCallback(
    (newDateRange: DateRange | undefined) => {
      if (!newDateRange?.from || !newDateRange.to) {
        return;
      }

      const fromDate = format(newDateRange.from, 'yyyy-MM-dd');
      const toDate = format(newDateRange.to, 'yyyy-MM-dd');

      if (fromDate !== searchParams.get('from') || toDate !== searchParams.get('to')) {
        setSearchParams({ from: fromDate, to: toDate }, { replace: true });
      }
    },
    [searchParams, setSearchParams]
  );

  return <DatePicker value={dateRange} onChange={handleDateChange} />;
}
